/**
 * ConversationCreditQuote: the "6 / message" line beside a conversation composer. One
 * component for the home composer and the in-room composer, so a level change shows the same
 * price in both places.
 */

import { useTranslation } from 'react-i18next'

import { CreditIcon } from '@/components/icons/CreditIcon'
import { useConversationModelEnabled } from '@/hooks/useConversationModelEnabled'
import { formatCreditAmount } from '@/lib/billing/formatCreditAmount'
import { cn } from '@/lib/utils'

import {
  CONVERSATION_LEVEL_CREDIT_MULTIPLIER,
  CONVERSATION_MODEL_TIER,
  CONVERSATION_TIER_CREDIT_MULTIPLIER,
  ConversationLevel,
  ConversationModel,
  conversationMessageCost,
} from '@digital-twins/shared-types'

export interface ConversationCreditRateInput {
  /** Base credits per message before any multiplier. Unknown yields no rate. */
  baseRate?: number | null
  /** Whether the viewer may use the advanced list (a stored pick is ignored otherwise). */
  isAdvancedEnabled: boolean
  level: ConversationLevel
  advancedModel?: ConversationModel | null
}

/** The credits one message costs for the selected level or advanced model, or null when unknown. */
export function conversationCreditRate(input: ConversationCreditRateInput): number | null {
  const { baseRate, isAdvancedEnabled, level, advancedModel } = input

  if (typeof baseRate !== 'number') {
    return null
  }
  const multiplier =
    advancedModel && isAdvancedEnabled
      ? CONVERSATION_TIER_CREDIT_MULTIPLIER[CONVERSATION_MODEL_TIER[advancedModel]]
      : CONVERSATION_LEVEL_CREDIT_MULTIPLIER[level]
  return conversationMessageCost(baseRate, multiplier)
}

export interface ConversationCreditQuoteProps {
  rate: number | null
  testId: string
  className?: string
}

export function ConversationCreditQuote(props: ConversationCreditQuoteProps) {
  const { rate, testId, className } = props

  const { t } = useTranslation('app')

  if (rate === null) {
    return null
  }

  return (
    <span
      data-testid={testId}
      className={cn(
        'inline-flex shrink-0 items-center gap-1 text-xs font-medium select-none',
        'text-[var(--home-credit-rate-fg)]',
        className,
      )}
    >
      <CreditIcon className="h-3 w-3" />
      {t('conversations.perMessage', { count: Number(formatCreditAmount(rate)) })}
    </span>
  )
}

export interface NewVideoPriceNoteProps {
  /** Credits this message costs with the new video (already multiplied). */
  credits: number
  /** The room's new-video multiplier (from the room DTO). */
  multiplier: number
  className?: string
}

/**
 * The composer note for a message that adds a video the room has not watched (credit metering
 * D15). Shown before sending, at every width, with the doubled price.
 */
export function NewVideoPriceNote(props: NewVideoPriceNoteProps) {
  const { credits, multiplier, className } = props

  const { t } = useTranslation('app')

  return (
    <span
      data-testid="chat-composer-new-video-note"
      className={cn('inline-flex items-center gap-1 text-xs text-muted-foreground', className)}
    >
      <CreditIcon className="h-3 w-3" />
      {formatCreditAmount(credits)}
      <span>{t('conversations.newVideoNote', { multiplier })}</span>
    </span>
  )
}

export interface RoomCreditQuoteProps {
  /** Multiplier of a message that carries a new video (the room DTO's `videoMultiplier`). */
  videoMultiplier?: number | null
  /** True when the message being written adds a video the room has not watched. */
  hasNewVideo?: boolean
  baseRate?: number | null
  level: ConversationLevel
  advancedModel?: ConversationModel | null
  testId?: string
  className?: string
}

/** The quote for a room's selected level or advanced pick, resolved with the viewer's own flags. */
export function RoomCreditQuote(props: RoomCreditQuoteProps) {
  const {
    baseRate,
    level,
    advancedModel,
    videoMultiplier = null,
    hasNewVideo = false,
    testId = 'chat-composer-credit-quote',
    className,
  } = props

  const isAdvancedEnabled = useConversationModelEnabled()

  const plainRate = conversationCreditRate({ baseRate, isAdvancedEnabled, level, advancedModel })
  const rate =
    plainRate !== null && hasNewVideo && typeof videoMultiplier === 'number' && videoMultiplier > 1
      ? plainRate * videoMultiplier
      : plainRate

  return <ConversationCreditQuote rate={rate} testId={testId} className={className} />
}
